import { Guest } from "../api/contracts/category-ads";
import { Guest as HeaderGuest } from "../api/contracts/reservation";
import { formatInTimeZone } from "date-fns-tz";
import { toasty } from "../components/Toast";
import { ApiError } from "../api/api-backend";
import i18next from "i18next";

type SearchGuest = Guest | HeaderGuest;

export const splitChildAndInfantGuests = (guests: SearchGuest[]) => {
  const children = guests.filter(
    (guest): guest is SearchGuest => guest.ageType === "child",
  );
  const infants = guests.filter(
    (guest): guest is SearchGuest => guest.ageType === "infant",
  );
  return { children, infants };
};

export const transformGuestsForSearch = (
  childGuests: SearchGuest[],
  infantGuests: SearchGuest[],
): {
  children: Guest[];
  infants: Guest[];
} => {
  const transformedChildGuests: Guest[] = childGuests.map((guest) => ({
    ...(guest.age !== undefined && { age: guest.age }),
    ageType: guest.ageType,
    bedCapacityType: guest.bedCapacityType,
  }));

  const transformedInfantGuests: Guest[] = infantGuests.map((guest) => ({
    ...(guest.age !== undefined && { age: guest.age }),
    ageType: guest.ageType,
    bedCapacityType: guest.bedCapacityType,
  }));

  return {
    children: transformedChildGuests,
    infants: transformedInfantGuests,
  };
};

/**
 * One staged unit's occupancy in the shape the search/pricing endpoints want.
 *
 * Shared so every surface that prices a unit builds the SAME object: these go
 * into the react-query key, and a landing page and a mobile sheet that differ
 * by one stray guest field ask for two cache entries where one would do.
 */
export const guestDetailsFromUnit = (unit?: {
  guests?: SearchGuest[] | null;
}): {
  adults: number;
  teenagers: number;
  children: Guest[];
  infants: Guest[];
} => {
  const guests = unit?.guests ?? [];
  const { children: childList, infants: infantList } =
    splitChildAndInfantGuests(guests);
  const { children, infants } = transformGuestsForSearch(childList, infantList);

  return {
    adults: guests.filter((g) => g.ageType === "adult").length,
    teenagers: guests.filter((g) => g.ageType === "teenager").length,
    children,
    infants,
  };
};

// TODO: Get timezone from site config - currently hardcoded to Europe/Stockholm
export const formatDateForAPI = (date: Date): string => {
  return formatInTimeZone(date, "Europe/Stockholm", "yyyy-MM-dd");
};

export const handleMutationError = (error: unknown) => {
  if (error instanceof ApiError && error.key) {
    // Look up a key-specific title (error.{key}-title) before falling back
    // to the generic "something went wrong". Race-condition outcomes like a
    // category becoming fully booked deserve their own non-alarming title.
    const titleKey = `error.${error.key}-title`;
    const resolvedTitle = i18next.t(titleKey);
    toasty({
      title:
        resolvedTitle === titleKey
          ? i18next.t("error.something_went_wrong")
          : resolvedTitle,
      description: i18next.t(`error.${error.key}`, error.params ?? {}),
      variant: "error",
    });
  } else if (error instanceof Error) {
    toasty({
      title: i18next.t("error.something_went_wrong"),
      description: error.message,
      variant: "error",
    });
  } else {
    toasty({
      title: i18next.t("error.something_went_wrong"),
      variant: "error",
    });
  }
};
